import React, { Component } from 'react';
import { Box, Typography } from '@mui/material';
import SectionTitle from './SectionTitle';
import DayTimeRangeInput from '../../../../../components/DayTimeRangeInput';
import DayPickerDialog from '../../../../../components/DayPickerDialog';
import DeletableChip from '../../../../../components/DeletableChip';
import { ALERT_SEVERITY } from '../../../../../components/AlertDisplay2';

const ADDED_BG_COLOR = 'rgba(255, 149, 47, 0.12)';

const DAY_LABEL_KEYS = {
    MON: 'MONDAY',
    TUE: 'TUESDAY',
    WED: 'WEDNESDAY',
    THU: 'THURSDAY',
    FRI: 'FRIDAY',
    SAT: 'SATURDAY',
    SUN: 'SUNDAY',
};

const DAY_LABEL_INDEX = {
    MON: 0,
    TUE: 1,
    WED: 2,
    THU: 3,
    FRI: 4,
    SAT: 5,
    SUN: 6,
};

const DAY_INDEX_LABEL = {
    0: 'MON',
    1: 'TUE',
    2: 'WED',
    3: 'THU',
    4: 'FRI',
    5: 'SAT',
    6: 'SUN',
};

const DAY_ORDER = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

const DAY_LABELS_DE = {
    MONDAY: 'Montag',
    TUESDAY: 'Dienstag',
    WEDNESDAY: 'Mittwoch',
    THURSDAY: 'Donnerstag',
    FRIDAY: 'Freitag',
    SATURDAY: 'Samstag',
    SUNDAY: 'Sonntag',
};

class TimeConfigSection extends Component {
    constructor(props) {
        super(props);

        this.state = {
            dayPickerSelection: [-1, -1],
            blockedDays: [],
            dayPickerOpen: false,
            currentDayPickerIndex: 0,
        };

        if (Array.isArray(props.config) && props.config.length > 0) {
            this.syncConfig(props.config);
        }
    }

    syncConfig = (config) => {
        const sorted = [...config].sort(
            (a, b) => DAY_ORDER.indexOf(a.dow) - DAY_ORDER.indexOf(b.dow)
        );

        const blockedDays = [];

        sorted.forEach((c) => {
            if (!c.deleted && !blockedDays.includes(DAY_LABEL_INDEX[c.dow]) && !c.added) {
                blockedDays.push(DAY_LABEL_INDEX[c.dow]);
            }
        });

        this.setState({ blockedDays });

        if (JSON.stringify(sorted) !== JSON.stringify(this.props.config)) {
            this.props.setConfig?.(sorted);
        }
    };

    componentDidUpdate(prevProps) {
        if (JSON.stringify(prevProps.config) !== JSON.stringify(this.props.config)) {
            this.syncConfig(this.props.config);
        }
    }

    openDayPickerDialog = (index) => {
        this.setState({
            dayPickerOpen: true,
            currentDayPickerIndex: index,
        });
    };

    closeDayPickerDialog = () => {
        this.setState({ dayPickerOpen: false });
    };

    setDayPickerSelection = (daySelection) => {
        this.setState((prev) => {
            const next = [...prev.dayPickerSelection];
            next[prev.currentDayPickerIndex] = daySelection;
            return {
                dayPickerSelection: next,
                dayPickerOpen: false,
            };
        });
    };

    render() {
        const { config = [], editMode, showAlert } = this.props;
        const weekdayBegin = this.state.dayPickerSelection[0];

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <SectionTitle
                    icon={require('../../../../../assets/icons/workingtime.png')}
                    label="Zeiten / Tage"
                />

                <DayTimeRangeInput
                    dayPickerSelection={this.state.dayPickerSelection}
                    openDayPickerDialog={this.openDayPickerDialog}
                    showAlert={showAlert}
                    singleMode
                    addOpeningHour={(startHourDate, endHourDate) => {
                        if (config.length === 0 && editMode) {
                            showAlert?.('Die Daten können nicht bearbeitet werden.', ALERT_SEVERITY.WARNING);
                            return;
                        }

                        if (weekdayBegin === -1) {
                            showAlert?.('Bitte zuerst einen Wochentag auswählen.', ALERT_SEVERITY.WARNING);
                            return;
                        }

                        const index = config.findIndex((c) => c.dow === DAY_INDEX_LABEL[weekdayBegin]);
                        const newConfig = [...config];

                        if (index === -1) {
                            newConfig.push({
                                added: true,
                                start: startHourDate.toTimeString().substring(0, 5),
                                end: endHourDate.toTimeString().substring(0, 5),
                                dow: DAY_INDEX_LABEL[weekdayBegin],
                            });
                            this.syncConfig(newConfig);
                            return;
                        }

                        if (editMode && newConfig[index].added == null) {
                            showAlert?.('Die Daten können nicht bearbeitet werden.', ALERT_SEVERITY.WARNING);
                            return;
                        }

                        newConfig[index] = {
                            added: true,
                            start: startHourDate.toTimeString().substring(0, 5),
                            end: endHourDate.toTimeString().substring(0, 5),
                            dow: DAY_INDEX_LABEL[weekdayBegin],
                        };

                        this.syncConfig(newConfig);
                    }}
                />

                {config?.length > 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                        }}
                    >
                        {config
                            .filter((conf) => (editMode ? !conf.deleted : true))
                            .map((item, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        width: {
                                            xs: '100%',
                                            sm: 'calc(50% - 4px)',
                                        },
                                    }}
                                >
                                    <DeletableChip
                                        disabled={editMode && item.added == null}
                                        onDelete={() => {
                                            const newConfig = [...config];

                                            if (!editMode || item.added == null) {
                                                newConfig.splice(index, 1);
                                            } else {
                                                newConfig[index].deleted = true;
                                            }

                                            this.props.setConfig?.(newConfig);
                                        }}
                                        showAlert={showAlert}
                                        style={item.added ? { backgroundColor: ADDED_BG_COLOR } : null}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: 1,
                                                alignItems: 'center',
                                                minWidth: 0,
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontWeight: 'bold',
                                                    fontSize: 14,
                                                    flex: 1,
                                                }}
                                            >
                                                {DAY_LABELS_DE[DAY_LABEL_KEYS[item.dow]]}:
                                            </Typography>

                                            <Typography
                                                sx={{
                                                    fontSize: 14,
                                                    flex: 2,
                                                    fontFamily: 'monospace',
                                                }}
                                            >
                                                {item.start} - {item.end}
                                            </Typography>
                                        </Box>
                                    </DeletableChip>
                                </Box>
                            ))}
                    </Box>
                )}

                <DayPickerDialog
                    open={this.state.dayPickerOpen}
                    daySelection={this.state.dayPickerSelection[this.state.currentDayPickerIndex]}
                    blockedDays={this.state.blockedDays}
                    currentPickerIndex={this.state.currentDayPickerIndex}
                    closeDialog={this.closeDayPickerDialog}
                    setDayPickerSelection={this.setDayPickerSelection}
                    showAlert={showAlert}
                />
            </Box>
        );
    }
}

export default TimeConfigSection;